import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  CalendarBlank,
  Timer,
  Fire,
  SmileyMeh,
  SmileyNervous,
  Smiley,
  MapPin,
  Note
} from '@phosphor-icons/react';

const CARDIO_TYPES = [
  { id: 'walking', label: 'Walking' },
  { id: 'running', label: 'Running' },
  { id: 'treadmill', label: 'Treadmill' },
  { id: 'cycling', label: 'Cycling' },
  { id: 'elliptical', label: 'Elliptical' },
  { id: 'stair_stepper', label: 'Stair Stepper' },
  { id: 'dance_cardio', label: 'Dance Cardio' },
  { id: 'jump_rope', label: 'Jump Rope' },
  { id: 'swimming', label: 'Swimming' },
  { id: 'rowing', label: 'Rowing' },
  { id: 'hiit', label: 'HIIT' },
  { id: 'other', label: 'Other' },
];

const INTENSITIES = ['Low', 'Moderate', 'High', 'Very High'];
const MOODS = [
  { id: 'tired', label: 'Tired', icon: SmileyNervous },
  { id: 'okay', label: 'Okay', icon: SmileyMeh },
  { id: 'great', label: 'Great', icon: Smiley },
];

const CardioLog = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    cardio_type: '',
    duration_minutes: '',
    distance: '',
    calories_burned: '',
    intensity: '',
    mood: '',
    notes: '',
  });

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cardio_type || !formData.duration_minutes) {
      toast.error('Please fill in cardio type and duration');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/cardio-logs`, {
        ...formData,
        duration_minutes: parseInt(formData.duration_minutes),
        distance: formData.distance ? parseFloat(formData.distance) : null,
        calories_burned: formData.calories_burned ? parseInt(formData.calories_burned) : null,
      }, { withCredentials: true, headers });

      toast.success('Workout logged!');
      navigate('/session-complete', { state: { log: formData } });
    } catch (error) {
      toast.error('Failed to log workout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-background pb-8 hexagon-bg"
      data-testid="cardio-log-screen"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-card flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Log Cardio
            </h1>
            <p className="text-xs text-muted-foreground">Record your workout details</p>
          </div>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-6 py-6 space-y-6">
        {/* Date */}
        <div className="premium-card p-4">
          <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
            <CalendarBlank weight="duotone" className="w-4 h-4" />
            Date
          </Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => updateForm('date', e.target.value)}
            className="bg-background border-border rounded-xl"
            data-testid="date-input"
          />
        </div>

        {/* Cardio Type */}
        <div className="premium-card p-4">
          <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 block">
            Cardio Type *
          </Label>
          <Select value={formData.cardio_type} onValueChange={(v) => updateForm('cardio_type', v)}>
            <SelectTrigger className="bg-background border-border rounded-xl py-6" data-testid="cardio-type-select">
              <SelectValue placeholder="Select cardio type" />
            </SelectTrigger>
            <SelectContent>
              {CARDIO_TYPES.map((type) => (
                <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Duration & Distance */}
        <div className="grid grid-cols-2 gap-4">
          <div className="premium-card p-4">
            <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
              <Timer weight="duotone" className="w-4 h-4" />
              Duration *
            </Label>
            <div className="relative">
              <Input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => updateForm('duration_minutes', e.target.value)}
                placeholder="30"
                className="bg-background border-border rounded-xl pr-14"
                required
                data-testid="duration-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">min</span>
            </div>
          </div>

          <div className="premium-card p-4">
            <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
              <MapPin weight="duotone" className="w-4 h-4" />
              Distance
            </Label>
            <div className="relative">
              <Input
                type="number"
                step="0.1"
                value={formData.distance}
                onChange={(e) => updateForm('distance', e.target.value)}
                placeholder="2.5"
                className="bg-background border-border rounded-xl pr-14"
                data-testid="distance-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">mi</span>
            </div>
          </div>
        </div>

        {/* Calories */}
        <div className="premium-card p-4">
          <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
            <Fire weight="duotone" className="w-4 h-4" />
            Calories Burned
          </Label>
          <div className="relative">
            <Input
              type="number"
              value={formData.calories_burned}
              onChange={(e) => updateForm('calories_burned', e.target.value)}
              placeholder="250"
              className="bg-background border-border rounded-xl pr-14"
              data-testid="calories-input"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">cal</span>
          </div>
        </div>

        {/* Intensity */}
        <div className="premium-card p-4">
          <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 block">
            Intensity
          </Label>
          <div className="flex gap-2">
            {INTENSITIES.map((intensity) => (
              <button
                key={intensity}
                type="button"
                onClick={() => updateForm('intensity', intensity)}
                className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                  formData.intensity === intensity
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-background border border-border text-muted-foreground hover:border-[#D4AF37]/50'
                }`}
                data-testid={`intensity-${intensity.toLowerCase()}`}
              >
                {intensity}
              </button>
            ))}
          </div>
        </div>

        {/* Mood */}
        <div className="premium-card p-4">
          <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 block">
            How did you feel?
          </Label>
          <div className="flex gap-3">
            {MOODS.map((mood) => {
              const Icon = mood.icon;
              return (
                <button
                  key={mood.id}
                  type="button"
                  onClick={() => updateForm('mood', mood.id)}
                  className={`flex-1 py-4 rounded-xl flex flex-col items-center gap-2 transition-all ${
                    formData.mood === mood.id
                      ? 'bg-[#D4AF37]/10 border-2 border-[#D4AF37]'
                      : 'bg-background border border-border hover:border-[#D4AF37]/50'
                  }`}
                  data-testid={`mood-${mood.id}`}
                >
                  <Icon weight={formData.mood === mood.id ? 'fill' : 'duotone'} className={`w-8 h-8 ${formData.mood === mood.id ? 'text-[#D4AF37]' : 'text-muted-foreground'}`} />
                  <span className={`text-xs ${formData.mood === mood.id ? 'text-[#D4AF37]' : 'text-muted-foreground'}`}>{mood.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div className="premium-card p-4">
          <Label className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
            <Note weight="duotone" className="w-4 h-4" />
            Notes
          </Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => updateForm('notes', e.target.value)}
            placeholder="How was your workout? Any notes..."
            className="bg-background border-border rounded-xl min-h-[100px] resize-none"
            data-testid="notes-input"
          />
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-6 hover:bg-[#F3E5AB] transition-all gold-glow gold-glow-hover"
          data-testid="save-log-btn"
        >
          {loading ? 'Saving...' : 'Save Workout'}
        </Button>
      </form>
    </div>
  );
};

export default CardioLog;
